import type { Maybe } from '@adornis/base/utilTypes.js';
import {
  isOrgAdminOfTargetUser,
  isOrgAdminPageCheck,
  isOrgMemberPageCheck,
  isSelf,
  organizationChecks,
  type TargetUserData,
} from '../checks.js';
import type { PageCheckable } from '../policyHelper.js';
import { defineSaraaScope } from './scopeFactory.js';

/** Anything owned by an organization: templates, template fields, processes. */
interface OrgOwned extends PageCheckable {
  _id?: Maybe<string>;
  organizationId?: Maybe<string>;
}

/** The organization itself — here the organization id *is* the entity id. */
interface OrgEntity extends PageCheckable {
  _id?: Maybe<string>;
}

const byOrganizationId = organizationChecks<OrgOwned>(data => data.organizationId);
const byOwnId = organizationChecks<OrgEntity>(data => data._id);

export const Templates = defineSaraaScope<OrgOwned>()({
  scope: 'templates',
  actions: ['view', 'create', 'modify', 'delete'] as const,
  checks: {
    view: { isOrgMemberOf: byOrganizationId.isOrgMemberOf, isOrgMemberPageCheck },
    create: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
    modify: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
    delete: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
  },
});

export const TemplateFields = defineSaraaScope<OrgOwned>()({
  scope: 'templateFields',
  actions: ['view', 'create', 'modify', 'delete'] as const,
  checks: {
    view: { isOrgMemberOf: byOrganizationId.isOrgMemberOf, isOrgMemberPageCheck },
    create: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
    modify: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
    delete: { isOrgAdminOf: byOrganizationId.isOrgAdminOf, isOrgAdminPageCheck },
  },
});

export const Organizations = defineSaraaScope<OrgEntity>()({
  scope: 'organizations',
  // create/delete are intentionally admin-only: an ORG_ADMIN edits the
  // organization they administer but cannot bring new ones into existence.
  actions: ['view', 'create', 'modify', 'delete'] as const,
  checks: {
    view: { isOrgMemberOf: byOwnId.isOrgMemberOf, isOrgMemberPageCheck },
    modify: { isOrgAdminOf: byOwnId.isOrgAdminOf, isOrgAdminPageCheck },
  },
});

export const Users = defineSaraaScope<TargetUserData>()({
  scope: 'users',
  actions: ['view', 'create', 'modify', 'delete', 'manageMemberships', 'changePassword'] as const,
  checks: {
    view: { isOrgAdminOfTargetUser, isSelf, isOrgAdminPageCheck },
    create: { isOrgAdminOfTargetUser, isOrgAdminPageCheck },
    modify: { isOrgAdminOfTargetUser, isSelf, isOrgAdminPageCheck },
    delete: { isOrgAdminOfTargetUser, isOrgAdminPageCheck },
    /**
     * Separate from `modify`, and deliberately without `isSelf`: editing your own
     * profile is ordinary, granting yourself a membership is escalation. Sharing
     * an action between the two would let anyone hand themselves ORG_ADMIN of any
     * organization through the membership mutations.
     */
    manageMemberships: { isOrgAdminOfTargetUser, isOrgAdminPageCheck },
    /**
     * Setting somebody's password is taking their account, so it gets an action of
     * its own rather than riding along on `modify` — the two can then be moved
     * apart later without touching the endpoints.
     *
     * **No page check on purpose.** Every other action here registers one so ERM
     * can show or hide a route with no entity to reason about; a page check
     * carries no target, and "may reset a password" with nobody named is not a
     * question worth answering true. The one caller always knows whose password it
     * is, so the check that decides is always the targeted one.
     */
    changePassword: { isOrgAdminOfTargetUser, isSelf },
  },
});

export const Generate = defineSaraaScope<OrgOwned>()({
  scope: 'generate',
  actions: ['use'] as const,
  checks: { use: { isOrgMemberOf: byOrganizationId.isOrgMemberOf, isOrgMemberPageCheck } },
});
